// src/store/slices/coordinateGridSlice.ts
/**
 * Coordinate Grid Redux Slice
 * 
 * Manages state for the 2D coordinate grid view mode including:
 * - Grid visibility and styling
 * - Axis configuration
 * - Quadrant visibility
 * - Scale locking
 * 
 * Phase 1: Core infrastructure
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../types/redux';
import {
  CoordinateGridState,
  GridStyle,
  GridDensity,
  AxisSettings,
  QuadrantVisibility,
  AxisLabelFormat,
  QuadrantPreset,
  getQuadrantPreset,
  DEFAULT_COORDINATE_GRID_STATE,
  DEFAULT_AXIS_SETTINGS,
} from '../../types/coordinateGrid';

// LocalStorage key for persistence
const STORAGE_KEY = 'nl:coordinateGrid';

/**
 * Load state from localStorage
 */
function loadFromStorage(): CoordinateGridState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COORDINATE_GRID_STATE;
    
    const parsed = JSON.parse(raw);
    
    // Merge with defaults to handle missing properties from older versions
    return {
      ...DEFAULT_COORDINATE_GRID_STATE,
      ...parsed,
      axisSettings: {
        ...DEFAULT_AXIS_SETTINGS,
        ...parsed.axisSettings,
      },
      quadrantsVisible: {
        ...DEFAULT_COORDINATE_GRID_STATE.quadrantsVisible,
        ...parsed.quadrantsVisible,
      },
    };
  } catch (error) {
    console.warn('Failed to load coordinate grid state from storage:', error);
    return DEFAULT_COORDINATE_GRID_STATE;
  }
}

/**
 * Save state to localStorage
 */
function saveToStorage(state: CoordinateGridState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save coordinate grid state:', error);
  }
}

// Initialize state from storage
const initialState: CoordinateGridState = loadFromStorage();

export const coordinateGridSlice = createSlice({
  name: 'coordinateGrid',
  initialState,
  reducers: {
    // =========================================================================
    // Grid Settings
    // =========================================================================
    
    /**
     * Toggle grid visibility
     */
    toggleGrid: (state) => {
      state.showGrid = !state.showGrid;
      saveToStorage(state);
    },

    /**
     * Set grid visibility
     */
    setShowGrid: (state, action: PayloadAction<boolean>) => {
      state.showGrid = action.payload;
      saveToStorage(state);
    },

    /**
     * Set grid style (lines, dots, or none)
     */
    setGridStyle: (state, action: PayloadAction<GridStyle>) => {
      state.gridStyle = action.payload;
      saveToStorage(state);
    },

    /**
     * Set grid density
     */
    setGridDensity: (state, action: PayloadAction<GridDensity>) => {
      state.gridDensity = action.payload;
      saveToStorage(state);
    },

    /**
     * Set grid color
     */
    setGridColor: (state, action: PayloadAction<string>) => {
      state.gridColor = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle minor gridlines
     */
    toggleMinorGridlines: (state) => {
      state.showMinorGridlines = !state.showMinorGridlines;
      saveToStorage(state);
    },

    /**
     * Set minor gridlines visibility
     */
    setShowMinorGridlines: (state, action: PayloadAction<boolean>) => {
      state.showMinorGridlines = action.payload;
      saveToStorage(state);
    },

    /**
     * Set minor grid color
     */
    setMinorGridColor: (state, action: PayloadAction<string>) => {
      state.minorGridColor = action.payload;
      saveToStorage(state);
    },

    // =========================================================================
    // Axis Settings
    // =========================================================================

    /**
     * Toggle X-axis visibility
     */
    toggleXAxis: (state) => {
      state.axisSettings.showXAxis = !state.axisSettings.showXAxis;
      saveToStorage(state);
    },

    /**
     * Toggle Y-axis visibility
     */
    toggleYAxis: (state) => {
      state.axisSettings.showYAxis = !state.axisSettings.showYAxis;
      saveToStorage(state);
    },

    /**
     * Set X-axis visibility
     */
    setShowXAxis: (state, action: PayloadAction<boolean>) => {
      state.axisSettings.showXAxis = action.payload;
      saveToStorage(state);
    },

    /**
     * Set Y-axis visibility
     */
    setShowYAxis: (state, action: PayloadAction<boolean>) => {
      state.axisSettings.showYAxis = action.payload;
      saveToStorage(state);
    },

    /**
     * Set X-axis label
     */
    setXLabel: (state, action: PayloadAction<string>) => {
      state.axisSettings.xLabel = action.payload;
      saveToStorage(state);
    },

    /**
     * Set Y-axis label
     */
    setYLabel: (state, action: PayloadAction<string>) => {
      state.axisSettings.yLabel = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle axis arrows
     */
    toggleAxisArrows: (state) => {
      state.axisSettings.showArrows = !state.axisSettings.showArrows;
      saveToStorage(state);
    },

    /**
     * Set axis arrows visibility
     */
    setShowAxisArrows: (state, action: PayloadAction<boolean>) => {
      state.axisSettings.showArrows = action.payload;
      saveToStorage(state);
    },

    /**
     * Set tick interval
     */
    setTickInterval: (state, action: PayloadAction<number>) => {
      const interval = Math.max(0.1, Math.min(10, action.payload));
      state.axisSettings.tickInterval = interval;
      saveToStorage(state);
    },

    /**
     * Set axis label format
     */
    setLabelFormat: (state, action: PayloadAction<AxisLabelFormat>) => {
      state.axisSettings.labelFormat = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle X-axis labels
     */
    toggleXLabels: (state) => {
      state.axisSettings.showXLabels = !state.axisSettings.showXLabels;
      saveToStorage(state);
    },

    /**
     * Toggle Y-axis labels
     */
    toggleYLabels: (state) => {
      state.axisSettings.showYLabels = !state.axisSettings.showYLabels;
      saveToStorage(state);
    },

    /**
     * Update all axis settings at once
     */
    setAxisSettings: (state, action: PayloadAction<Partial<AxisSettings>>) => {
      state.axisSettings = {
        ...state.axisSettings,
        ...action.payload,
      };
      saveToStorage(state);
    },

    /**
     * Set axis color
     */
    setAxisColor: (state, action: PayloadAction<string>) => {
      state.axisColor = action.payload;
      saveToStorage(state);
    },

    /**
     * Set axis thickness
     */
    setAxisThickness: (state, action: PayloadAction<number>) => {
      state.axisThickness = Math.max(1, Math.min(5, action.payload));
      saveToStorage(state);
    },

    // =========================================================================
    // Quadrant Visibility
    // =========================================================================

    /**
     * Set quadrant visibility using a preset
     */
    setQuadrantPreset: (state, action: PayloadAction<QuadrantPreset>) => {
      state.quadrantsVisible = getQuadrantPreset(action.payload);
      saveToStorage(state);
    },

    /**
     * Toggle a specific quadrant
     */
    toggleQuadrant: (state, action: PayloadAction<'q1' | 'q2' | 'q3' | 'q4'>) => {
      const quadrant = action.payload;
      state.quadrantsVisible[quadrant] = !state.quadrantsVisible[quadrant];
      
      // Ensure at least one quadrant is visible
      const { q1, q2, q3, q4 } = state.quadrantsVisible;
      if (!q1 && !q2 && !q3 && !q4) {
        state.quadrantsVisible[quadrant] = true;
      }
      
      saveToStorage(state);
    },

    /**
     * Set quadrant visibility directly
     */
    setQuadrantVisibility: (state, action: PayloadAction<Partial<QuadrantVisibility>>) => {
      state.quadrantsVisible = {
        ...state.quadrantsVisible,
        ...action.payload,
      };
      
      // Ensure at least one quadrant is visible
      const { q1, q2, q3, q4 } = state.quadrantsVisible;
      if (!q1 && !q2 && !q3 && !q4) {
        state.quadrantsVisible.q1 = true;
      }
      
      saveToStorage(state);
    },

    // =========================================================================
    // Scale Settings
    // =========================================================================

    /**
     * Toggle equal scaling lock
     */
    toggleEqualScaling: (state) => {
      state.equalScaling = !state.equalScaling;
      saveToStorage(state);
    },

    /**
     * Set equal scaling
     */
    setEqualScaling: (state, action: PayloadAction<boolean>) => {
      state.equalScaling = action.payload;
      saveToStorage(state);
    },

    // =========================================================================
    // Origin Marker
    // =========================================================================

    /**
     * Toggle origin marker visibility
     */
    toggleOriginMarker: (state) => {
      state.showOriginMarker = !state.showOriginMarker;
      saveToStorage(state);
    },

    /**
     * Set origin marker visibility
     */
    setShowOriginMarker: (state, action: PayloadAction<boolean>) => {
      state.showOriginMarker = action.payload;
      saveToStorage(state);
    },

    // =========================================================================
    // Bulk Operations
    // =========================================================================

    /**
     * Reset to default settings
     */
    resetCoordinateGrid: (state) => {
      Object.assign(state, DEFAULT_COORDINATE_GRID_STATE);
      saveToStorage(state);
    },

    /**
     * Load coordinate grid state from serialized data (e.g., shared scene)
     */
    loadCoordinateGridState: (state, action: PayloadAction<Partial<CoordinateGridState>>) => {
      Object.assign(state, {
        ...DEFAULT_COORDINATE_GRID_STATE,
        ...action.payload,
        axisSettings: {
          ...DEFAULT_AXIS_SETTINGS,
          ...action.payload.axisSettings,
        },
        quadrantsVisible: {
          ...DEFAULT_COORDINATE_GRID_STATE.quadrantsVisible,
          ...action.payload.quadrantsVisible,
        },
      });
      saveToStorage(state);
    },
  },
});

// Export actions
export const {
  // Grid settings
  toggleGrid,
  setShowGrid,
  setGridStyle,
  setGridDensity,
  setGridColor,
  toggleMinorGridlines,
  setShowMinorGridlines,
  setMinorGridColor,
  // Axis settings
  toggleXAxis,
  toggleYAxis,
  setShowXAxis,
  setShowYAxis,
  setXLabel,
  setYLabel,
  toggleAxisArrows,
  setShowAxisArrows,
  setTickInterval,
  setLabelFormat,
  toggleXLabels,
  toggleYLabels,
  setAxisSettings,
  setAxisColor,
  setAxisThickness,
  // Quadrant visibility
  setQuadrantPreset,
  toggleQuadrant,
  setQuadrantVisibility,
  // Scale settings
  toggleEqualScaling,
  setEqualScaling,
  // Origin marker
  toggleOriginMarker,
  setShowOriginMarker,
  // Bulk operations
  resetCoordinateGrid,
  loadCoordinateGridState,
} = coordinateGridSlice.actions;

// =========================================================================
// Selectors
// =========================================================================

export const selectCoordinateGrid = (state: RootState) => state.coordinateGrid;
export const selectShowGrid = (state: RootState) => state.coordinateGrid.showGrid;
export const selectGridStyle = (state: RootState) => state.coordinateGrid.gridStyle;
export const selectGridDensity = (state: RootState) => state.coordinateGrid.gridDensity;
export const selectAxisSettings = (state: RootState) => state.coordinateGrid.axisSettings;
export const selectQuadrantsVisible = (state: RootState) => state.coordinateGrid.quadrantsVisible;
export const selectEqualScaling = (state: RootState) => state.coordinateGrid.equalScaling;

export default coordinateGridSlice.reducer;
